'use client'

import {Accordion, Box, Stack, Text} from '@blocks'
import {
   type FAQ,
   faq,
   siteSectionBodyHex,
   siteSectionHeadingHex,
   siteTextOnDarkBandHex,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {FaqAnswer} from '@/blocks/site/FaqAnswer'

const faqAccordionRoot = {
   width: '100%',
   maxWidth: '100%',
   minWidth: 0,
   gap: 8
} as const

/**
 * FAQ on a **light** band (#f4f7ff, etc.) — white rows, navy/gray copy (readable).
 */
export const faqAccordionStylesOnLightParent = {
   root: faqAccordionRoot,
   item: {
      backgroundColor: '#ffffff',
      border: '1px solid #d6e0f0',
      borderRadius: 12,
      overflow: 'hidden' as const,
      boxShadow: '0 1px 3px rgba(10, 55, 121, 0.07)'
   },
   control: {
      color: siteSectionHeadingHex,
      backgroundColor: 'transparent',
      padding: '20px 22px',
      '&:hover': {
         backgroundColor: 'rgba(10, 55, 121, 0.05)'
      }
   },
   panel: {
      padding: '10px 22px 22px',
      color: siteSectionBodyHex,
      backgroundColor: 'transparent'
   },
   content: {
      overflowWrap: 'anywhere' as const,
      wordBreak: 'break-word' as const
   },
   chevron: {
      color: siteSectionHeadingHex
   }
}

/**
 * FAQ on a **dark** navy band — near-white panels, dark question/answer text.
 */
export const faqAccordionStylesOnDarkParent = {
   root: faqAccordionRoot,
   item: {
      backgroundColor: 'rgba(255,255,255,0.96)',
      border: '1px solid rgba(255,255,255,0.22)',
      borderRadius: 12,
      overflow: 'hidden' as const
   },
   control: {
      color: siteSectionHeadingHex,
      backgroundColor: 'transparent',
      padding: '20px 22px',
      '&:hover': {
         backgroundColor: 'rgba(255,255,255,0.75)'
      }
   },
   panel: {
      padding: '10px 22px 22px',
      color: siteSectionBodyHex,
      backgroundColor: 'transparent'
   },
   content: {
      overflowWrap: 'anywhere' as const,
      wordBreak: 'break-word' as const
   },
   chevron: {
      color: siteSectionHeadingHex
   }
}

/** @deprecated Use faqAccordionStylesOnLightParent */
export const faqAccordionStylesDarkPanels =
   faqAccordionStylesOnLightParent

/** @deprecated Use faqAccordionStylesOnDarkParent */
export const faqAccordionStylesLightPanels =
   faqAccordionStylesOnDarkParent

/** @deprecated Use faqAccordionStylesOnLightParent */
export const faqAccordionStyles = faqAccordionStylesOnLightParent

type AccordionPayload = Readonly<{
   categoryPriority: FAQ['category']
   /**
    * `dark` = FAQ on dark navy — light panels, dark text.
    * `light` = FAQ on pale band — white panels, dark text.
    */
   parentSurface?: 'light' | 'dark'
}>

export const FaqAccordion = ({
   categoryPriority,
   parentSurface = 'dark'
}: AccordionPayload) => {
   const categoryGroup = faq.find(
      (group) => group.category === categoryPriority
   )

   if (!categoryGroup || categoryGroup.items.length === 0) {
      return null
   }

   const isOnDarkParent = parentSurface === 'dark'
   const accordionStyles = isOnDarkParent
      ? faqAccordionStylesOnDarkParent
      : faqAccordionStylesOnLightParent
   const questionColor = siteSectionHeadingHex
   const answerVariant = 'default' as const

   return (
      <Accordion
         variant={'separated'}
         styles={accordionStyles}
      >
         {categoryGroup.items.map((item) => (
            <Accordion.Item
               key={item.question}
               value={item.question}
            >
               <Accordion.Control w={'100%'}>
                  <Text
                     fw={650}
                     fz={19}
                     c={questionColor}
                     lh={1.4}
                  >
                     {item.question}
                  </Text>
               </Accordion.Control>
               <Accordion.Panel pb={8}>
                  <FaqAnswer
                     answer={item.answer}
                     variant={answerVariant}
                  />
               </Accordion.Panel>
            </Accordion.Item>
         ))}
      </Accordion>
   )
}

type InSectionPayload = Readonly<{
   categoryPriority: FAQ['category']
   /**
    * `light` = pale band: white FAQ rows, dark text.
    * `dark` = navy band: light FAQ rows, dark text on white panels.
    */
   sectionTone?: 'light' | 'dark'
}>

/**
 * FAQ block inside a page section — no outer tint box, divider removed.
 */
export const FaqInSection = ({
   categoryPriority,
   sectionTone = 'light'
}: InSectionPayload) => {
   const isMobile = useMediaQuery('(max-width: 768px)')
   const categoryGroup = faq.find(
      (group) => group.category === categoryPriority
   )

   if (!categoryGroup || categoryGroup.items.length === 0) {
      return null
   }

   const isDarkParent = sectionTone === 'dark'
   const titleColor = isDarkParent
      ? siteTextOnDarkBandHex
      : siteSectionHeadingHex
   const parentSurface = isDarkParent ? 'dark' : 'light'

   return (
      <Stack
         gap={20}
         mt={{base: 56, sm: 88}}
         pt={{base: 32, sm: 40}}
      >
         <Text
            c={titleColor}
            fz={
               isMobile
                  ? siteTypographySectionTitleSecondaryFontSizeMobile
                  : siteTypographySectionTitleSecondaryFontSizeDesktop
            }
            fw={800}
         >
            Frequently asked questions
         </Text>
         <Box
            w={'100%'}
            style={{backgroundColor: 'transparent'}}
         >
            <FaqAccordion
               categoryPriority={categoryPriority}
               parentSurface={parentSurface}
            />
         </Box>
      </Stack>
   )
}
